import { Loading } from '@livecontrol/core-ui';
import type { Event, User } from '@livecontrol/scheduler/model';
import _ from 'lodash';
import type { ReactElement } from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { InfiniteScrollComponent } from '../../../components';
import { Store } from '../../../store';
import { EventData } from './event-data';

const EventDataContainer = styled.div`
  padding: 14px 0px 30px;

  .scroll-container {
    width: 100%;
    padding-right: 15px;
    padding-left: 15px;
    margin-right: auto;
    margin-left: auto;
  }

  .infinite-scroll-component__outerdiv {
    width: 100%;
  }
`;

interface Props {
  transformed: readonly Event[];
  hasMore: boolean;
  fetchMoreData: () => void;
  onDelete?: (event: Event) => void;
}

export const EventsSection = ({ transformed, onDelete, fetchMoreData, hasMore }: Props): ReactElement => {
  const transformedCopy = transformed.map((event) => ({ ...event }));
  const me = Store.User.useMe();
  const account = Store.Account.useAccount();
  const isAdmin = Store.User.useIsAdmin();
  const [openedDropdown, setOpenDropdown] = useState<Event['id'] | undefined>();

  const adminUser: User.admin = me as User.admin;

  const handleDropdown =
    (id: Event['id']) =>
    (state: boolean): void => {
      setOpenDropdown(state ? id : undefined);
    };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const showRequestBtnTier = isAdmin
    ? adminUser.userGlobalSettings.clientTier ?? ''
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    : account.userGlobalSettings?.clientTier ?? '';

  const linkedMobileKitEvents = transformedCopy.filter(
    (event) => event.linkedEventId && event.isMobileKit && event.phase !== 2
  );

  const deleteEventsIds = new Set<number>();

  linkedMobileKitEvents.forEach((event) => {
    let itsProducedEvent = transformedCopy.find(
      (produced) => produced.id === event.linkedEventId && produced.phase !== 2
    );

    if (itsProducedEvent) {
      if (!Object.isExtensible(itsProducedEvent)) {
        itsProducedEvent = { ...itsProducedEvent };
      }

      itsProducedEvent.linkedEventInfo = { ...event };
      deleteEventsIds.add(event.id);
    }
  });

  const finalEvents = transformedCopy.filter((event) => !deleteEventsIds.has(event.id));

  return (
    <EventDataContainer className='d-flex flex-wrap align-items-start justify-content-center'>
      <div className='d-flex flex-column align-items-start' style={{maxWidth: '100%', width: '100%'}}>
        <InfiniteScrollComponent
          dataLength={finalEvents.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={<Loading.Delay />}
          style={{ overflow: 'unset' }}
        >
          {finalEvents.map((event) => (
            <EventData
              key={event.id}
              event={event}
              dropdownState={openedDropdown === event.id}
              handleDropdown={handleDropdown(event.id)}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              showRequestBtnTier={showRequestBtnTier}
              onDelete={onDelete}
            />
          ))}
        </InfiniteScrollComponent>
      </div>
    </EventDataContainer>
  );
};
